import { useEffect, useMemo, useState, useSyncExternalStore } from "react";
import {
  Banner,
  BlockStack,
  Button,
  Card,
  InlineStack,
  Modal,
  Select,
  Text,
} from "@shopify/polaris";
import { useAuth } from "../App";
import {
  createShopifyRetry,
  queueAccountKey,
  queueRequest,
  type QueueAccount,
} from "../../../packages/shared/shopify-queue/client";
import { RetryHistory } from "./shopify-queue/RetryHistory";
const base = import.meta.env.VITE_API_URL || "https://api.whenlyapp.com";
const commands = new Map<string, ReturnType<typeof createShopifyRetry>>();
const storage = {
  get length() {
    return window.localStorage.length;
  },
  key: (index: number) => window.localStorage.key(index),
  getItem: (key: string) => window.localStorage.getItem(key),
  setItem: (key: string, value: string) =>
    window.localStorage.setItem(key, value),
  removeItem: (key: string) => window.localStorage.removeItem(key),
};
type Job = {
  id: string;
  kind: string;
  entity_id: string;
  inventory_domain?:string|null;
  status: string;
  attempts: number;
  error: string | null;
  revision: number;
  retry_count: number;
  next_attempt_at: string;
  updated_at: string;
  outcome: null | {
    skipped?: boolean;
    checkedAt?: string;
    locations?: { locationId: string; quantity: number; result: string }[];
  };
};
type Page = {
  jobs: Job[];
  nextCursor: number | null;
  total: number;
  unverified: number;
  counts: Record<string, number>;
  canRetry: boolean;
};
const labels: Record<string, string> = {
  pending: "Waiting to send",
  processing: "Sending or recovering",
  failed: "Needs attention",
  skipped: "Skipped",
  success: "Confirmed",
  privacy_removed: "Removed for privacy",
  cancelled: "Cancelled",
};
export function ShopifyJobs() {
  const { user, currentShop } = useAuth();
  const account = useMemo(
    () => ({
      userId: String(user?.id || ""),
      domain: currentShop?.domain || "",
      organizationId: currentShop?.organizationId,
    }),
    [user?.id, currentShop?.domain, currentShop?.organizationId],
  );
  if (!account.userId || !account.domain)
    return (
      <Card>
        <Text as="p">Select a company to view Shopify updates.</Text>
      </Card>
    );
  return <Queue key={queueAccountKey(account)} account={account} />;
}
function Queue({ account }: { account: QueueAccount }) {
  const key = queueAccountKey(account);
  if (!commands.has(key))
    commands.set(key, createShopifyRetry(base, account, storage));
  const command = commands.get(key)!;
  const state = useSyncExternalStore(command.subscribe, command.snapshot);
  const [filter, setFilter] = useState("attention"),
    [pages, setPages] = useState<number[]>([]),
    [refresh, setRefresh] = useState(0);
  const [data, setData] = useState<Page | null>(null),
    [error, setError] = useState(""),
    [review, setReview] = useState<Job | null>(null),
    [history, setHistory] = useState<string | null>(null);
  const cursor = pages[pages.length - 1],
    locked = state.busy || !!state.pending;
  useEffect(() => {
    const abort = new AbortController();
    setData(null);
    setError("");
    void queueRequest<Page>(
      base,
      account,
      `?status=${filter}${cursor ? "&cursor=" + cursor : ""}`,
      { signal: abort.signal },
    )
      .then((value) => {
        if (!abort.signal.aborted) setData(value);
      })
      .catch((e) => {
        if (!abort.signal.aborted) setError(e.message);
      });
    return () => abort.abort();
  }, [account, filter, cursor, refresh, state.success]);
  const changeFilter = (value: string) => {
    setFilter(value);
    setPages([]);
    setHistory(null);
  };
  return (
    <Card>
      <BlockStack gap="300">
        <Text as="h2" variant="headingMd">
          Shopify updates
        </Text>
        <Text as="p">
          Stock changes and shipments saved for Shopify. Skipped updates need
          their connection or mapping fixed before retrying.
        </Text>
        {error && <Banner tone="critical">{error}</Banner>}
        {state.error && <Banner tone="critical">{state.error}</Banner>}
        {state.success && (
          <Banner tone="success">
            Retry saved. This confirms it is queued, not that Shopify has
            accepted it.
          </Banner>
        )}
        {state.pending && (
          <Banner tone="warning">
            <BlockStack gap="200">
              <Text as="p">
                A saved retry for {state.pending.jobId} still needs a confirmed
                reply. It stays saved when you close this browser.
              </Text>
              <Button loading={state.busy} onClick={() => void command.retry()}>
                Retry saved request
              </Button>
            </BlockStack>
          </Banner>
        )}
        <InlineStack gap="300" align="space-between" blockAlign="end">
          <Select
            label="Show updates"
            value={filter}
            onChange={changeFilter}
            options={[
              { label: "Waiting, failed and skipped", value: "attention" },
              { label: "All updates", value: "all" },
              { label: "Failed", value: "failed" },
              { label: "Skipped", value: "skipped" },
              { label: "Waiting", value: "pending" },
              { label: "Sending", value: "processing" },
              { label: "Successful records", value: "success" },
              { label: "Older success without proof", value: "unverified" },
            ]}
          />
          <Button
            size="large"
            onClick={() => {
              setPages([]);
              setRefresh((v) => v + 1);
            }}
          >
            Refresh
          </Button>
        </InlineStack>
        {!data && !error && <Text as="p">Loading Shopify updates…</Text>}
        {data && (
          <>
            <Text as="p">
              {data.total} updates match this filter. Waiting:{" "}
              {data.counts.pending || 0}. Sending: {data.counts.processing || 0}
              . Failed: {data.counts.failed || 0}. Skipped:{" "}
              {data.counts.skipped || 0}.
            </Text>
            {data.unverified > 0 && (
              <Banner tone="info">
                {data.unverified} older successful records have no saved
                publication proof. Their Shopify result is unverified.
              </Banner>
            )}
            {!data.jobs.length && (
              <Text as="p">No updates match this filter.</Text>
            )}
            {data.jobs.map((job) => (
              <Card key={job.id}>
                <BlockStack gap="200">
                  <Text as="h3" variant="headingSm">
                    {job.kind === "inventory" ? "Stock" : "Shipment"}{" "}
                    {job.entity_id}:{" "}
                    {job.status === "success" && !job.outcome
                      ? "Older success, unverified"
                      : labels[job.status] || job.status}
                  </Text>
                  {job.kind === "inventory" && (
                    <Text as="p">Shopify store: {job.inventory_domain || "No recorded store"}.</Text>
                  )}
                  {job.kind === "inventory" && !job.inventory_domain && ["failed", "skipped"].includes(job.status) && (
                    <Text as="p">Review a new stock publication for the intended store.</Text>
                  )}
                  <Text as="p">
                    {job.attempts} total attempts. Last change: {job.updated_at}{" "}
                    UTC.
                  </Text>
                  {job.status === "pending" && (
                    <Text as="p">
                      Next automatic attempt from {job.next_attempt_at} UTC.
                    </Text>
                  )}
                  {job.error && (
                    <Text as="p" tone="critical">
                      {job.error}
                    </Text>
                  )}
                  {!!job.outcome?.locations?.length && (
                    <BlockStack gap="100">
                      {job.status !== "success" && (
                        <Text as="p">
                          Confirmed locations from the last attempt. The whole
                          update is not complete.
                        </Text>
                      )}
                      {job.outcome.locations.map((location) => (
                        <Text as="p" key={location.locationId}>
                          Location {location.locationId.split("/").pop()}:{" "}
                          {location.quantity} available,{" "}
                          {location.result === "matched"
                            ? "already matched"
                            : "updated"}
                          .
                        </Text>
                      ))}
                    </BlockStack>
                  )}
                  <InlineStack gap="200">
                    {data.canRetry && (job.kind !== "inventory" || !!job.inventory_domain) &&
                      ["failed", "skipped"].includes(job.status) && (
                        <Button
                          disabled={locked}
                          onClick={() => setReview(job)}
                        >
                          Review retry
                        </Button>
                      )}
                    {job.retry_count > 0 && (
                      <Button
                        onClick={() =>
                          setHistory(history === job.id ? null : job.id)
                        }
                      >{`Retry history (${job.retry_count})`}</Button>
                    )}
                  </InlineStack>
                  {history === job.id && (
                    <RetryHistory key={job.id} id={job.id} account={account} />
                  )}
                </BlockStack>
              </Card>
            ))}
            <InlineStack gap="200">
              {pages.length > 0 && (
                <Button onClick={() => setPages((p) => p.slice(0, -1))}>
                  Previous updates
                </Button>
              )}
              {data.nextCursor && (
                <Button
                  onClick={() => setPages((p) => [...p, data.nextCursor!])}
                >
                  Older updates
                </Button>
              )}
            </InlineStack>
          </>
        )}
        <Modal
          open={!!review}
          onClose={() => setReview(null)}
          title="Review Shopify retry"
          primaryAction={{
            content: "Queue retry",
            disabled: locked,
            onAction: () => {
              if (review) {
                void command.submit(review.id, review.revision);
                setReview(null);
              }
            },
          }}
          secondaryActions={[
            { content: "Cancel", onAction: () => setReview(null) },
          ]}
        >
          <Modal.Section>
            <BlockStack gap="200">
              <Text as="p">
                {review?.kind === "inventory"
                  ? `Send the current available warehouse stock to ${review.inventory_domain || "the original store (not recorded)"}.`
                  : "Resume the saved shipment. Shopify may notify the customer when it is fulfilled."}
              </Text>
              <Text as="p">
                {review?.error ||
                  "Check the issue before sending this update again."}
              </Text>
              <Text as="p">
                A shipment with an unknown earlier result is checked in Shopify
                before it can be sent again.
              </Text>
            </BlockStack>
          </Modal.Section>
        </Modal>
      </BlockStack>
    </Card>
  );
}
